import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverPosition, PopoverType, Select, SelectState, usePopoverHandlers, usePrevious, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { BasketButtonError, NotifyMeError, } from '@as-react/common-types';
import { isEqual } from '@as-react/common-utils';
import { Box, ConditionalWrapper, Label, Stack, useMediaQueryMatchesOnDesktop } from '@yonderland/andes-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { mapSizeSelectOptions } from '../../../util/ProductUtil';
import { ScrollUtil } from '../../../util/ScrollUtil';
import { SizeDropdown } from '../../size-dropdown/SizeDropdown';
import BasketQuantityField from '../basket-quantity-field/BasketQuantityField';
import { SizeRecommendationV2 } from '../size-recommendation-v2/SizeRecommendationV2';
const SCROLLABLE_LIST_SELECTOR = '.as-m-scrollable-list__scroll-container';
export const SizeSelectV2 = ({ id, basketValidationState, sizes, selectedSku, selectSize, setBasketError, setQuantity, sizeRecommendation, isOneSizeProduct, isOneSizeProductInline, isStickyBar, isHiddenStickyBar, isModal, showQuantityField = false, quantity, openSizeSelectOnError, onClose, children, isSelectedSizeSoldOut, notifyMeError, }) => {
    var _a, _b;
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const SIZE_SELECT_NAME = isStickyBar ? 'size-select-sticky' : 'size-select';
    const DEFAULT_SIZE_SELECT_CLASS = `.as-a-select__select[name=${SIZE_SELECT_NAME}]`;
    const { isShopLanding } = useAemConfig();
    const { filteredSizes, options } = mapSizeSelectOptions(sizes, true, undefined, !isShopLanding);
    const SHOW_ERROR = isStickyBar
        ? basketValidationState === BasketButtonError.SKU_STICKY_BAR
        : basketValidationState === BasketButtonError.SKU;
    const [shouldShowError, setShouldShowError] = useState(false);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [buttonEl, setButtonEl] = useState();
    const [wrapperEl, setWrapperEl] = useState();
    const [isDropdownOpen, setIsDropdownOpen] = useState();
    const sizeSelectRef = useRef(null);
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const { anchorEl, handlePopoverOpen, handlePopoverClose } = usePopoverHandlers();
    const prevSizes = usePrevious(sizes);
    const handleSelectedItemChange = (option) => {
        setShouldShowError(false);
        selectSize({ sku: option.value.toString(), isSoldOut: option.isSoldOut });
    };
    const openPopover = () => {
        if (!isPopoverOpen && buttonEl) {
            setIsPopoverOpen(true);
            handlePopoverOpen(null, buttonEl, false, true);
        }
    };
    const closePopover = () => {
        if (isPopoverOpen && buttonEl) {
            setIsPopoverOpen(false);
            handlePopoverClose();
            setButtonEl(undefined);
        }
    };
    const onSizeSelectClose = () => {
        setIsDropdownOpen(false);
        onClose && onClose();
    };
    useEffect(() => {
        if (isOneSizeProduct && selectedSku) {
            selectSize({ sku: selectedSku });
        }
    }, []);
    useEffect(() => {
        if (!isEqual(sizes, prevSizes)) {
            setShouldShowError(false);
        }
    }, [sizes]);
    useEffect(() => {
        if (selectedSku && shouldShowError) {
            setShouldShowError(false);
        }
    }, [selectedSku]);
    useEffect(() => {
        if (isHiddenStickyBar && isPopoverOpen && setBasketError) {
            setBasketError();
        }
    }, [isHiddenStickyBar, isPopoverOpen]);
    useEffect(() => {
        const sizeSelectEl = document.querySelector(DEFAULT_SIZE_SELECT_CLASS);
        setButtonEl(sizeSelectEl);
        if (openSizeSelectOnError &&
            (basketValidationState === BasketButtonError.SKU_OPEN_SIZE_SELECT || notifyMeError === NotifyMeError.SELECT_SIZE)) {
            ScrollUtil.scrollToErrorElement(sizeSelectEl, isDesktop, () => {
                var _a;
                (_a = sizeSelectRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                setIsDropdownOpen(true);
            });
        }
        else {
            setShouldShowError(SHOW_ERROR);
            if (SHOW_ERROR && buttonEl) {
                openPopover();
                if (basketValidationState === BasketButtonError.SKU) {
                    if (isModal) {
                        ScrollUtil.scrollToElement(buttonEl, true);
                    }
                    else {
                        ScrollUtil.scrollToErrorElement(sizeSelectEl, isDesktop);
                    }
                }
            }
            closePopover();
        }
    }, [basketValidationState, buttonEl, notifyMeError]);
    useEffect(() => {
        if (isModal && buttonEl && !wrapperEl) {
            const wrapperElement = buttonEl.closest(SCROLLABLE_LIST_SELECTOR);
            setWrapperEl(wrapperElement);
        }
    }, [buttonEl, isModal, wrapperEl]);
    const setPopoverPosition = () => {
        if (!isStickyBar) {
            return isDesktop ? PopoverPosition.LEFT : PopoverPosition.BOTTOM;
        }
        return isDesktop ? PopoverPosition.BOTTOM : PopoverPosition.TOP;
    };
    if (isOneSizeProduct && showQuantityField) {
        return (_jsx(Stack, { children: _jsx(Box, { flex: { basis: '1/4' }, children: _jsx(BasketQuantityField, { quantity: Number(quantity), setQuantity: setQuantity }) }) }));
    }
    if (isOneSizeProductInline) {
        return (_jsxs(_Fragment, { children: [sizeRecommendation && _jsx(SizeRecommendationV2, { sizeRecommendation: sizeRecommendation }), _jsx(Label, { children: t('pdp.size') }), " ", (_a = options[0]) === null || _a === void 0 ? void 0 : _a.label] }));
    }
    if (isOneSizeProduct) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [!isStickyBar && sizeRecommendation && _jsx(SizeRecommendationV2, { sizeRecommendation: sizeRecommendation }), _jsxs(ConditionalWrapper, { condition: showQuantityField, wrapper: wrapperChildren => _jsx(Stack, { align: "end", children: wrapperChildren }), children: [_jsxs(Box, { flex: { grow: 1 }, paddingInlineEnd: showQuantityField ? '2' : undefined, children: [!isStickyBar && _jsx(Label, { children: t('pdp.size') }), _jsx(Box, { paddingBlock: !isStickyBar ? '2' : undefined, children: _jsx(SizeDropdown, { id: id, toggleElementRef: sizeSelectRef, isOpen: isDropdownOpen, onClose: onSizeSelectClose, onOpen: () => setIsDropdownOpen(true), appendToBody: isStickyBar || isModal, stickyWrapper: isStickyBar || isModal, sizes: filteredSizes, options: options, selectedSku: selectedSku, handleSelectedItemChange: handleSelectedItemChange, toggleElement: _jsx(Select, { dataQA: "size_select", name: SIZE_SELECT_NAME, options: options, defaultOptionValue: options.length === 1 ? (_b = options[0]) === null || _b === void 0 ? void 0 : _b.value : undefined, selectedValue: selectedSku, placeholder: t('pdp.size.choose.default'), fullWidth: true, disabled: true, state: shouldShowError ? SelectState.ERROR : undefined, color: !isShopLanding && isSelectedSizeSoldOut ? 'N300' : undefined, strike: !isShopLanding && isSelectedSizeSoldOut, children: children }) }) })] }), showQuantityField && (_jsx(Box, { flex: { basis: '1/4', shrink: 0 }, paddingInlineStart: "2", children: _jsx(BasketQuantityField, { quantity: Number(quantity), setQuantity: setQuantity }) }))] }), _jsx(Popover, { anchorEl: anchorEl, onClose: handlePopoverClose, scrollableWrapperEl: wrapperEl, showTitleOnDesktop: false, position: setPopoverPosition(), stickyWrapper: isStickyBar || isModal, type: PopoverType.TOOLTIP, noAnimation: true, children: t(isStickyBar ? 'stickyBar.sizeSelect.label' : 'pdp.selectSize.label') })] }));
};
